// --- Imports ---
import Alpine from "alpinejs";
import Swiper from "swiper";
import { Autoplay, FreeMode, Navigation, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// --- Make Alpine globally available ASAP ---
window.Alpine = Alpine;
document.addEventListener("alpine:init", () => {
  // Global layout store
  Alpine.store("layout", {
    headerH: 0,
    setHeaderH(h) {
      this.headerH = Math.max(0, Number(h) || 0);
    },
  });
});
// --- Swiper: text-image (fade) ---
function initTextImageSliders(root = document) {
  root
    .querySelectorAll("[data-text-image-swiper]:not([data-swiper-ready])")
    .forEach((wrap) => {
      const swiperEl = wrap.querySelector(".swiper");
      const nextEl = wrap.querySelector("[data-next]");
      const prevEl = wrap.querySelector("[data-prev]");
      if (!swiperEl) return;

      const modules = [EffectFade];
      const config = {
        effect: "fade",
        fadeEffect: { crossFade: true },
        loop: true,
        speed: 600,
        grabCursor: true,
        allowTouchMove: true,
        autoHeight: false,
      };
      if (nextEl && prevEl) {
        modules.push(Navigation);
        config.navigation = { nextEl, prevEl };
      }

      new Swiper(swiperEl, { modules, ...config });
      wrap.dataset.swiperReady = "1";
    });
}

// --- Swiper: two-up (fade between) ---
function initTwoUpSliders(root = document) {
  root
    .querySelectorAll('.swiper[id^="two-up-"]:not([data-swiper-ready])')
    .forEach((swiperEl) => {
      const id = swiperEl.id;
      const wrap =
        swiperEl.closest(".relative") || swiperEl.parentElement || document;
      const nextEl = wrap.querySelector(`.two-up-next-${id}`);
      const prevEl = wrap.querySelector(`.two-up-prev-${id}`);
      const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;

      new Swiper(swiperEl, {
        modules: [Navigation, EffectFade],
        effect: "fade",
        fadeEffect: { crossFade: true },
        speed: 600,
        autoHeight: false,
        allowTouchMove: false,
        grabCursor: false,
        loop: slideCount > 1,
        loopAdditionalSlides: 1,
        loopPreventsSliding: false,
        navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
      });
      swiperEl.dataset.swiperReady = "1";
    });
}

// --- Marquee on scroll ---
function initScrollMarquees(root = document) {
  const prefersReduced = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReduced) return;

  root
    .querySelectorAll("[data-scroll-marquee]:not([data-marquee-ready])")
    .forEach((wrap) => {
      const track = wrap.querySelector("[data-track]");
      if (!track) return;

      let width = 1;
      let ticking = false;
      const speed = parseFloat(wrap.dataset.speed || "0.2");

      function measure() {
        width = track.scrollWidth || 1;
      }

      function update() {
        const offset = (-window.scrollY * speed) % width;
        track.style.transform = `translate3d(${offset}px, 0, 0)`;
        ticking = false;
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      function onResize() {
        measure();
        update();
      }

      measure();
      update();

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);

      wrap.dataset.marqueeReady = "1";
    });
}

// --- Social scroller ---
export function initSocialScroller(root = document) {
  root
    .querySelectorAll("[data-social-scroller]:not([data-swiper-ready])")
    .forEach((wrap) => {
      const el = wrap.querySelector(".swiper");
      if (!el) return;

      new Swiper(el, {
        modules: [Autoplay, FreeMode],
        slidesPerView: "auto",
        spaceBetween: 0,
        loop: true,
        speed: 12000,
        freeMode: { enabled: true, momentum: false },
        allowTouchMove: true,
        autoplay: {
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
      });

      wrap.setAttribute("data-swiper-ready", "");
    });
}

// --- Alpine component: revealOnScroll (register BEFORE Alpine.start) ---
Alpine.data("revealOnScroll", (opts = {}) => ({
  options: {
    root: null,
    rootMargin: opts.rootMargin || "0px 0px -10% 0px",
    threshold: opts.threshold ?? 0.01,
    staggerStep: opts.staggerStep ?? 100,
  },
  io: null,

  async init() {
    const root = this.$el;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const all = root.querySelectorAll(".reveal");

    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);

    if (reduce) {
      all.forEach((el) => el.classList.add("inview"));
      return;
    }

    all.forEach((el) => el.classList.add("is-ready"));

    root.querySelectorAll("[data-stagger]").forEach((group) => {
      const step = Number(group.dataset.stagger) || this.options.staggerStep;
      [...group.querySelectorAll(".reveal")].forEach((el, i) => {
        const hasDelayClass = [...el.classList].some((c) =>
          c.startsWith("delay-")
        );
        if (!el.style.getPropertyValue("--reveal-delay") && !hasDelayClass) {
          el.style.setProperty("--reveal-delay", `${i * step}ms`);
        }
      });
    });

    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("inview");
        this.io.unobserve(entry.target);
      });
    }, this.options);

    all.forEach((el) => this.io.observe(el));
  },
}));

// --- Alpine component: cateringForm (register BEFORE Alpine.start) ---
Alpine.data("cateringForm", () => ({
  step: 1,
  data: {
    firstName: "",
    lastName: "",
    company: "",
    fromEmail: "",
    phone: "",
    eventDate: "",
    eventTime: "",
    guests: "",
    service: "",
    dietary: "",
    address1: "",
    address2: "",
    city: "",
    postcode: "",
    island: "",
    sandwiches: Array(5).fill(""),
    message: "",
  },
  next() {
    if (this.step === 1) {
      if (!this.data.firstName || !this.data.lastName || !this.data.fromEmail) {
        alert("Please enter your first name, last name and email.");
        return;
      }
    }
    if (this.step === 2) {
      if (!this.data.eventDate || !this.data.eventTime) {
        alert("Please select an event date and time.");
        return;
      }
    }
    if (this.step === 3) {
      if (
        !this.isCollection() &&
        (!this.data.address1 || !this.data.city || !this.data.postcode)
      ) {
        alert("Please enter your address, city and postcode.");
        return;
      }
    }
    if (this.step === 4) {
      const requiredCount = this.requiredSandwichCount();
      const selections = this.data.sandwiches.filter((s) => s && s.trim());
      if (selections.length < requiredCount) {
        alert(
          `Please choose at least ${requiredCount} ${requiredCount === 1 ? "sandwich" : "sandwiches"}.`
        );
        return;
      }
    }
    if (this.step < 5) this.step++;
    this.scrollToTop();
  },
  requiredSandwichCount() {
    return this.isCollection() ? 1 : 5;
  },
  isCollection() {
    return this.data.service.toLowerCase() === "collection";
  },
  prev() {
    if (this.step > 1) this.step--;
    this.scrollToTop();
  },
  onSubmit(form) {
    form.submit();
  },
  scrollToTop() {
    const section = this.$refs.cateringFormSection;
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80,
        behavior: "smooth",
      });
    }
  },
}));

// --- Start Alpine (after all data components are registered) ---
Alpine.start();

// --- Non-Alpine DOM inits ---
document.addEventListener("DOMContentLoaded", () => {
  initTextImageSliders();
  initTwoUpSliders();
  initScrollMarquees();
  initSocialScroller();
});

// --- Optional: warn if Alpine is duplicated elsewhere (e.g., CDN in layout) ---
if (document.querySelectorAll('script[src*="alpinejs"]').length > 0) {
  // Your bundle already includes Alpine. Remove any CDN script tags from templates.
  console.warn(
    "[Alpine] Detected an additional Alpine script tag on the page. Remove the CDN include to avoid scope errors."
  );
}
